import React from "react";
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from "@mui/material";

import {
    ScorecardBaselineTable,
    ScorecardBaselineTableData,
} from "../../../models/Scorecard";
import { scorecardBaselineTableMetrics } from "../../../constants/Scorecard";
import { formatPercentageNoRoundOff } from "../../../utils";
import { categoryTableCell, headerCell, labelTableCell, renderCellStyle, scoreHeader, scoreTableCell } from "../styles/style";

export interface PropsFromState {
    scorecardBaselineTableState: ScorecardBaselineTable | null;
}

const BaselineTable: React.FC<PropsFromState> = ({
    scorecardBaselineTableState,
}) => {
    const rows = scorecardBaselineTableState?.baselineTableData ?? [];

    const scores = rows.length
        ? [...new Set(rows.map((r) => Number(r.score)).filter((n) => !isNaN(n)))].sort(
            (a, b) => a - b
        )
        : [0, 1, 2, 3];

    const scoreMap: Record<string, ScorecardBaselineTableData> = {};
    rows.forEach((row) => {
        scoreMap[String(row.score)] = row;
    });

    const numericOnlyFields = new Set([
        "careStatStart",
        "careStatEnd",
        "starRatingStart",
        "starRatingEnd",
    ]);

    const renderCell = (
        score: number,
        key: keyof ScorecardBaselineTableData
    ) => {
        const value = scoreMap[String(score)]?.[key];

        if (value === null || value === undefined) return "-";

        const num = Number(value);
        if (isNaN(num)) return "-";

        return numericOnlyFields.has(key)
            ? num.toFixed(2)
            : formatPercentageNoRoundOff(num);
    };

    return (
        <TableContainer>
            <Table size="small">
                <TableHead sx={scoreHeader}>
                    <TableRow>
                        <TableCell
                            sx={scoreTableCell}
                        >
                            Score
                        </TableCell>

                        {scores.map((score) => (
                            <TableCell
                                key={score}
                                colSpan={2}
                                align="center"
                                sx={scoreHeader}
                            >
                                {score}
                            </TableCell>
                        ))}
                    </TableRow>

                    <TableRow>
                        <TableCell
                            sx={categoryTableCell}
                        >
                            Category
                        </TableCell>
                        {scores.map((score) => (
                            <React.Fragment key={`limits-${score}`}>
                                <TableCell sx={headerCell}>
                                    Lower Limit
                                </TableCell>
                                <TableCell sx={headerCell}>
                                    Upper Limit
                                </TableCell>
                            </React.Fragment>
                        ))}
                    </TableRow>
                </TableHead>

                <TableBody>
                    {scorecardBaselineTableMetrics.map(({ group, rows }) => (
                        <React.Fragment key={group}>
                            <TableRow>
                                <TableCell colSpan={1 + scores.length * 2} sx={headerCell}>
                                    {group}
                                </TableCell>
                            </TableRow>
                            {rows.map(({ label, start, end }) => (
                                <TableRow key={label}>
                                    <TableCell sx={labelTableCell}>{label}</TableCell>
                                    {scores.map((score) => (
                                        <React.Fragment key={`${label}-${score}`}>
                                            <TableCell sx={renderCellStyle}>{renderCell(score, start)}</TableCell>
                                            <TableCell sx={renderCellStyle}>{renderCell(score, end)}</TableCell>
                                        </React.Fragment>
                                    ))}
                                </TableRow>
                            ))}
                        </React.Fragment>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default BaselineTable;